<template>
  <section class="dialer-pre-call">
    <section class="dialer-pre-call__container">
      <!-- Coluna Esquerda - Preview de Vídeo -->
      <section class="dialer-pre-call__video-card">
        <div class="dialer-pre-call__video-wrapper">
          <video
            ref="videoPreview"
            autoplay
            muted
            playsinline
            :class="[
              'dialer-pre-call__video',
              { 'dialer-pre-call__video--hidden': !isCameraActive },
            ]"
          ></video>
          <div v-if="!isCameraActive" class="dialer-pre-call__placeholder">
            <i class="fas fa-video-slash dialer-pre-call__placeholder-icon"></i>
            <p class="dialer-pre-call__placeholder-text">Câmera desativada</p>
          </div>

          <div class="dialer-pre-call__video-controls">
            <IconButton
              icon="camera"
              :title="isCameraActive ? 'Desativar câmera' : 'Ativar câmera'"
              :initialActive="isCameraActive"
              @toggle="handleCameraTest"
            />

            <IconButton
              icon="microphone"
              :title="
                isMicrophoneActive ? 'Desativar microfone' : 'Ativar microfone'
              "
              :initialActive="isMicrophoneActive"
              @toggle="handleMicrophoneTest"
            />
          </div>
        </div>
      </section>

      <!-- Coluna Direita - Configurações -->
      <section class="dialer-pre-call__settings-card">
        <section class="dialer-pre-call__box">
          <div class="dialer-pre-call__settings-header">
            <h2 class="dialer-pre-call__title">
              Preparando para Iniciar a Chamada
            </h2>
            <p class="dialer-pre-call__subtitle">
              {{
                textOptions
                  ? textOptions
                  : "Entre na chamada após testar seu microfone e câmera."
              }}
            </p>
          </div>

          <div v-if="errorMessage" class="dialer-pre-call__error">
            <i
              class="fas fa-exclamation-triangle dialer-pre-call__error-icon"
            ></i>
            <p class="dialer-pre-call__error-text">{{ errorMessage }}</p>
          </div>

          <div class="dialer-pre-call__settings-body">
            <Select
              @select="onSelectMicrophone"
              :initialValue="microphoneSelected"
              :options="[microphoneSelected]"
              title="Microfone Selecionado"
            />
            <Select
              @select="onSelectCamera"
              :initialValue="cameraSelected"
              :options="[cameraSelected]"
              title="Câmera Selecionada"
            />
            <Select
              @select="onSelectAudio"
              :initialValue="audioSelected"
              :options="[audioSelected]"
              title="Viva-voz"
            />
          </div>

          <div class="dialer-pre-call__audio-level">
            <div class="dialer-pre-call__audio-bar">
              <div
                class="dialer-pre-call__audio-fill"
                :style="{ width: audioLevel + '%' }"
              ></div>
            </div>
            <label class="dialer-pre-call__label">Nível do Microfone</label>
          </div>
        </section>

        <section class="dialer-pre-call__box">
          <div class="dialer-pre-call__settings-footer">
            <TextButton
              icon="next"
              title="Entrar na Chamada"
              @click="handleStartCall"
            />
          </div>
        </section>
      </section>
    </section>
  </section>
</template>

<script>
import "./DialerPreCall.css";

import IconButton from "../../../components/icon-button/IconButton.vue";
import TextButton from "../../../components/text-button/TextButton.vue";
import Select from "../../../components/select/Select.vue";

const VIDEO_CONSTRAINTS = {
  WIDTH: { ideal: 1280 },
  HEIGHT: { ideal: 720 },
  FACING_MODE: "user",
};
const AUDIO_CONSTRAINTS = {
  ECHO_CANCELLATION: true,
  NOISE_SUPPRESSION: true,
  AUTO_GAIN_CONTROL: true,
};
const AUDIO_ANALYSIS_CONFIG = {
  FFT_SIZE: 256,
  MAX_VALUE: 255,
  AMPLIFICATION_FACTOR: 2,
  MAX_LEVEL: 100,
};

export default {
  name: "DialerPreCall",
  components: {
    IconButton,
    TextButton,
    Select,
  },

  props: {
    primaryUID: {
      type: [String, Number],
      required: true,
      validator(value) {
        return true;
      },
    },
    secondaryUID: {
      type: [String, Number],
      required: true,
      validator(value) {
        return true;
      },
    },
    options: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isCameraActive: true,
      isMicrophoneActive: false,
      audioLevel: 0,
      videoStream: null,
      audioStream: null,
      audioContext: null,
      analyser: null,
      animationFrameId: null,
      errorMessage: "",

      microphoneSelected: {
        value: "default",
        label: "Microfone Padrão",
        icon: "fas fa-microphone",
      },
      cameraSelected: {
        value: "default",
        label: "Câmera Padrão",
        icon: "fas fa-video",
      },
      audioSelected: {
        value: "default",
        label: "Viva-voz Padrão",
        icon: "fas fa-volume-up",
      },
    };
  },

  computed: {
    textOptions() {
      const options = this.options || {};
      const parts = [];
      if (options.date) parts.push(`${options.date}`);
      if (options.hour) parts.push(`${options.hour}`);
      if (options.name) parts.push(`${options.name}`);
      if (!parts.length) return "";
      return (
        parts[0] +
        parts
          .slice(1)
          .map((p) => ` | ${p}`)
          .join("")
      );
    },
  },

  mounted() {
    console.info(
      `[Dialer][PreCall] DE: (primary) ${this.primaryUID} PARA: (secondary) ${this.secondaryUID}`,
    );
    this._validateUIDsFormat();

    this._startCamera();
  },

  beforeUnmount() {
    this._cleanupMediaResources();
    console.info("[Dialer][PreCall] Desmontando");
  },

  methods: {
    /**
     * Manipula o teste de câmera (liga/desliga)
     * @public
     */
    async handleCameraTest() {
      const wasActive = this.isCameraActive;

      if (wasActive) {
        this._stopCamera();
      } else {
        await this._startCamera();
      }

      this._emitEvent("testCamera", { status: !wasActive });
    },

    /**
     * Manipula o teste de microfone (liga/desliga)
     * @public
     */
    async handleMicrophoneTest() {
      const wasActive = this.isMicrophoneActive;

      if (wasActive) {
        this._stopMicrophone();
      } else {
        await this._startMicrophone();
      }

      this._emitEvent("testMicrophone", { status: !wasActive });
    },

    /**
     * Inicia a chamada após validação e limpeza
     * @public
     */
    handleStartCall() {
      console.info("[Dialer][PreCall] Iniciando chamada...");

      if (!this._validateRoomData()) {
        return;
      }

      this._cleanupMediaResources();
      this._emitCallEvents();
    },

    onSelectMicrophone(value) {
      console.info("[Dialer][PreCall] Microfone selecionado:", value);
      this.microphoneSelected = value;
    },
    onSelectCamera(value) {
      console.info("[Dialer][PreCall] Câmera selecionada:", value);
      this.cameraSelected = value;
    },
    onSelectAudio(value) {
      console.info("[Dialer][PreCall] Áudio selecionado:", value);
      this.audioSelected = value;
    },

    /**
     * Inicia o stream da câmera
     * @private
     * @async
     * @returns {Promise<void>}
     */
    async _startCamera() {
      try {
        console.info("[Dialer][PreCall] Iniciando câmera...");

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: VIDEO_CONSTRAINTS.WIDTH,
            height: VIDEO_CONSTRAINTS.HEIGHT,
            facingMode: VIDEO_CONSTRAINTS.FACING_MODE,
          },
          audio: false,
        });

        this.videoStream = stream;

        this.$nextTick(() => {
          if (this.$refs.videoPreview) {
            this.$refs.videoPreview.srcObject = stream;
          }
        });

        console.info("[Dialer][PreCall] Câmera iniciada com sucesso");
        this.isCameraActive = true;
      } catch (error) {
        console.error("[Dialer][PreCall] Erro ao iniciar câmera:", error);
        this.isCameraActive = false;
        this._showError(
          "Erro ao acessar a câmera. Verifique as permissões do navegador.",
        );
      }
    },

    /**
     * Para o stream da câmera e limpa recursos
     * @private
     */
    _stopCamera() {
      if (this.videoStream) {
        console.info("[Dialer][PreCall] Parando câmera...");

        this.videoStream.getTracks().forEach((track) => track.stop());
        this.videoStream = null;
        this.isCameraActive = false;

        if (this.$refs.videoPreview) {
          this.$refs.videoPreview.srcObject = null;
        }
      }
    },

    /**
     * Inicia o stream do microfone e análise de áudio
     * @private
     * @async
     * @returns {Promise<void>}
     */
    async _startMicrophone() {
      try {
        console.info("[Dialer][PreCall] Iniciando microfone...");

        const stream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            echoCancellation: AUDIO_CONSTRAINTS.ECHO_CANCELLATION,
            noiseSuppression: AUDIO_CONSTRAINTS.NOISE_SUPPRESSION,
            autoGainControl: AUDIO_CONSTRAINTS.AUTO_GAIN_CONTROL,
          },
        });

        this.audioStream = stream;
        this.isMicrophoneActive = true;

        this._initializeAudioAnalysis(stream);
        this._monitorAudioLevel();

        console.info("[Dialer][PreCall] Microfone iniciado com sucesso");
      } catch (error) {
        console.error("[Dialer][PreCall] Erro ao iniciar microfone:", error);
        this._showError(
          "Erro ao acessar o microfone. Verifique as permissões do navegador.",
        );
      }
    },

    /**
     * Para o stream do microfone e limpa recursos de áudio
     * @private
     */
    _stopMicrophone() {
      if (this.audioStream) {
        console.info("[Dialer][PreCall] Parando microfone...");

        this.audioStream.getTracks().forEach((track) => track.stop());
        this.audioStream = null;
        this.isMicrophoneActive = false;
        this.audioLevel = 0;

        this._cleanupAudioAnalysis();
      }
    },

    /**
     * Inicializa o contexto de análise de áudio
     * @private
     * @param {MediaStream} stream - Stream de áudio
     */
    _initializeAudioAnalysis(stream) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = AUDIO_ANALYSIS_CONFIG.FFT_SIZE;

      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser);
    },

    /**
     * Monitora o nível de áudio do microfone em tempo real
     * @private
     */
    _monitorAudioLevel() {
      if (!this.analyser || !this.isMicrophoneActive) {
        return;
      }

      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateLevel = () => {
        if (!this.isMicrophoneActive) {
          return;
        }

        this.analyser.getByteFrequencyData(dataArray);

        const average = this._calculateAverageAudioLevel(
          dataArray,
          bufferLength,
        );
        this.audioLevel = this._normalizeAudioLevel(average);

        this.animationFrameId = requestAnimationFrame(updateLevel);
      };

      updateLevel();
    },

    /**
     * Calcula a média dos valores de áudio
     * @private
     * @param {Uint8Array} dataArray - Array de dados de áudio
     * @param {number} bufferLength - Tamanho do buffer
     * @returns {number} Média dos valores
     */
    _calculateAverageAudioLevel(dataArray, bufferLength) {
      return dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    },

    /**
     * Normaliza o nível de áudio para 0-100
     * @private
     * @param {number} average - Valor médio
     * @returns {number} Nível normalizado
     */
    _normalizeAudioLevel(average) {
      const normalized =
        (average / AUDIO_ANALYSIS_CONFIG.MAX_VALUE) *
        AUDIO_ANALYSIS_CONFIG.MAX_LEVEL *
        AUDIO_ANALYSIS_CONFIG.AMPLIFICATION_FACTOR;

      return Math.min(AUDIO_ANALYSIS_CONFIG.MAX_LEVEL, normalized);
    },

    /**
     * Limpa recursos de análise de áudio
     * @private
     */
    _cleanupAudioAnalysis() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
        this.analyser = null;
      }
    },

    /**
     * Para todos os streams e limpa todos os recursos
     * @private
     */
    _cleanupMediaResources() {
      this._stopCamera();
      this._stopMicrophone();
      console.info("[Dialer][PreCall] Recursos de mídia limpos");
    },

    /**
     * Valida os dados da sala antes de iniciar
     * @private
     * @returns {boolean} True se válido
     */
    _validateRoomData() {
      if (!this.primaryUID || !this.secondaryUID) {
        this._showError("Por favor, forneça as identidades (UID).");
        return false;
      }

      if (!this._validateUIDsFormat()) {
        return false;
      }

      this._clearError();
      return true;
    },

    /**
     * Valida o formato dos UIDs
     * @private
     * @returns {boolean} true se os UIDs são válidos, false caso contrário
     */
    _validateUIDsFormat() {
      const primaryUID = String(this.primaryUID ?? '').replace(/\D/g, '');
      const secondaryUID = String(this.secondaryUID ?? '').replace(/\D/g, '');
      if (!/^[0-9]{11}$/.test(primaryUID) || !/^[0-9]{11}$/.test(secondaryUID)) {
        this._showError("UIDs inválidos. Use exatamente 11 dígitos.");
        return false;
      }
      this.primaryUID = primaryUID;
      this.secondaryUID = secondaryUID;
      return true;
    },

    /**
     * Emite os eventos de início de chamada
     * @private
     */
    _emitCallEvents() {
      this._emitEvent("changeToCall", { status: true });

      this.$emit("start-call", {
        primaryUID: this.primaryUID,
        secondaryUID: this.secondaryUID,
      });
    },

    /**
     * Emite um evento de forma centralizada
     * @private
     * @param {string} eventName - Nome do evento
     * @param {*} data - Dados do evento
     */
    _emitEvent(eventName, data) {
      this.$emit("emit-event", { eventName, data });
      console.info(`[Dialer][PreCall] Evento emitido: ${eventName}`, data);
    },

    /**
     * Exibe uma mensagem de erro
     * @private
     * @param {string} message - Mensagem de erro a ser exibida
     */
    _showError(message) {
      this.errorMessage = message;
      console.error("[Dialer][PreCall] Erro:", message);
    },

    /**
     * Limpa a mensagem de erro
     * @private
     */
    _clearError() {
      this.errorMessage = "";
    },
  },
};
</script>
